import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import {
  PageHeaderCard,
  PageHeaderIdentity,
  PageHeaderTopRow,
} from '@/components/shared/PageHeaderCard';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  useCertificationStandardOptions,
  useCollaborationStatusOptions,
  useNetworkCompanyOptions,
} from '@/hooks/useDropdowns';
import { useTranslator } from '@/hooks/useTranslator';
import { CERTIFICATION_STATUS_BADGE } from '@/lib/constants/certifications';
import { useSessionStore } from '@/store/sessionStore';
import { CertDrilldownTable } from '../../../../../-components/certificate-compliance/CertDrilldownTable';
import {
  buildCertDrilldownChips,
  buildCertDrilldownFilter,
  parseFilterParam,
} from '../../../../../-utils/drilldownUtils';

export const Route = createFileRoute(
  '/(client)/report/(pages)/(drilldown)/certificate-compliance/drilldown/$chartType/',
)({
  // TanStack Router auto-parses JSON query params, so `filter` may arrive as
  // an already-parsed object (when the URL was built with URLSearchParams) or
  // as a raw string (when navigate() double-encodes it). Accept both.
  validateSearch: z.object({
    filter: z.union([z.string(), z.record(z.string(), z.unknown())]).optional(),
  }),
  component: CertDrilldownPage,
});

function CertDrilldownPage() {
  const { getTranslation: t } = useTranslator({ page: 'certCompliance' });
  const { chartType } = useParams({ strict: false }) as { chartType: string };
  const search = (useSearch({ strict: false }) ?? {}) as {
    filter?: string | Record<string, unknown>;
  };
  const companyId = useSessionStore(s => s.user?.companyId ?? '');

  const filterObj = parseFilterParam(search.filter);
  const drilldownFilter = buildCertDrilldownFilter(filterObj);

  // Load options to resolve IDs → display labels (matches old frontend behaviour)
  const { data: supplierOptions = [] } = useNetworkCompanyOptions();
  const { data: collabStatusOptions = [] } = useCollaborationStatusOptions();
  const { data: standardsRaw = [] } = useCertificationStandardOptions();
  const standardOptions = (standardsRaw as { id: string; name: string }[]).map(
    s => ({ key: s.id, label: s.name }),
  );

  const filterChips = buildCertDrilldownChips({
    filter: drilldownFilter,
    t,
    statusVariants: CERTIFICATION_STATUS_BADGE,
    supplierOptions,
    collabStatusOptions,
    standardOptions,
  });
  const chartTitle = t(`drilldown.chartType.${chartType}`) || chartType;

  return (
    <PageContainer className='flex flex-col gap-4 overflow-hidden'>
      <PageHeaderCard className='shrink-0'>
        <PageHeaderTopRow>
          <PageHeaderIdentity
            title={chartTitle}
            subtitle={t('drilldown.description')}
            // Undefined rather than an empty array — `badges` renders its row
            // whenever the prop is truthy, and `[]` is truthy.
            badges={
              filterChips.length > 0
                ? filterChips.map(chip =>
                    chip.variant ? (
                      <StatusBadge
                        key={chip.label}
                        label={chip.label}
                        variant={chip.variant}
                      />
                    ) : (
                      <Badge
                        key={chip.label}
                        variant='secondary'
                        className='rounded-md text-xs font-normal'
                      >
                        {chip.label}
                      </Badge>
                    ),
                  )
                : undefined
            }
          />
        </PageHeaderTopRow>
      </PageHeaderCard>
      <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
        <CertDrilldownTable companyId={companyId} filter={drilldownFilter} />
      </div>
    </PageContainer>
  );
}
